import {
    PinIcon as PinOnMapIcon,
    StarFilledIcon,
} from "@wags/ui/product-icons.tsx";

import { Fragment, type ReactElement } from "react";

import type { ProviderType } from "@wags/catalog/pure.ts";

import { cn } from "@wags/ui/ui-utils";
import { ProviderStatTiles } from "./provider-stat-tiles";
import { ProviderWebsiteLink } from "./provider-website-link";
import { reviewNoun, reviewsLine } from "./reviews-line";
import { ProviderVerifiedName } from "./provider-verified-name";

type ProviderHeaderData = {
    providerName?: string;
    providerType: ProviderType;
    isVerified?: boolean;
    breedSpecialty?: string;
    city?: string;
    distanceLabel?: string;
    reviewCount?: number | null;
    averageRating?: number | null;
    ratingCount?: number | null;
    reviewsHref?: string;
    inBusinessSinceYear?: number | null;
    website?: string;
};

function providerRating(data: ProviderHeaderData) {
    if (!data.averageRating || !data.ratingCount) return null;
    return { average: data.averageRating, count: data.ratingCount };
}

type ProviderRatingSummary = ReturnType<typeof providerRating>;

function getProviderStats(
    data: ProviderHeaderData,
    rating: ProviderRatingSummary,
) {
    const stats: { value: string; label: string }[] = [];
    if (
        !rating &&
        typeof data.reviewCount === "number" &&
        data.reviewCount > 0
    ) {
        stats.push({
            value: String(data.reviewCount),
            label: reviewNoun(data.reviewCount),
        });
    }
    const years = data.inBusinessSinceYear
        ? new Date().getFullYear() - data.inBusinessSinceYear
        : 0;
    if (years > 0) {
        stats.push({ value: `${years} yrs`, label: "in business" });
    }

    return stats;
}

function ProviderLocation({
    city,
    distanceLabel,
}: Pick<ProviderHeaderData, "city" | "distanceLabel">) {
    return (
        <p className="flex min-w-0 items-start gap-1.5">
            {city && <PinOnMapIcon className="mt-0.5 size-4 shrink-0" />}
            <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                {city && (
                    <span className="min-w-0 text-pretty wrap-anywhere">
                        {city}
                    </span>
                )}
                {city && distanceLabel && <span aria-hidden="true">·</span>}
                {distanceLabel && (
                    <span className="whitespace-nowrap">{distanceLabel}</span>
                )}
            </span>
        </p>
    );
}

function ProviderRating({
    average,
    count,
    href,
}: {
    average: number;
    count: number;
    href?: string;
}) {
    const content = (
        <>
            <StarFilledIcon
                aria-hidden="true"
                className="size-4 shrink-0 text-doberman-black/85"
            />
            <span className="font-medium tabular-nums text-doberman-black/85">
                {average.toFixed(1)}
            </span>
            <span>({reviewsLine(count)})</span>
        </>
    );
    const className = "inline-flex items-center gap-1 whitespace-nowrap";
    if (!href) return <span className={className}>{content}</span>;
    return (
        <a
            href={href}
            className={cn(
                className,
                "rounded-sm underline-offset-4 outline-offset-2 hover:underline focus-visible:outline-2",
            )}
        >
            {content}
        </a>
    );
}

const META_SEPARATOR = <span aria-hidden="true">·</span>;

function ProviderMetaRow({
    data,
    rating,
    stats,
}: {
    data: ProviderHeaderData;
    rating: ProviderRatingSummary;
    stats: ReturnType<typeof getProviderStats>;
}) {
    const reviewLabel =
        stats.length === 1 ? `${stats[0].value} ${stats[0].label}` : null;
    const hasLocation = Boolean(data.city || data.distanceLabel);
    const facts: { key: string; node: ReactElement }[] = [];
    if (rating) {
        facts.push({
            key: "rating",
            node: (
                <ProviderRating
                    average={rating.average}
                    count={rating.count}
                    href={data.reviewsHref}
                />
            ),
        });
    }
    if (reviewLabel) {
        facts.push({
            key: "stat",
            node: <span className="whitespace-nowrap">{reviewLabel}</span>,
        });
    }
    if (data.website) {
        facts.push({
            key: "website",
            node: <ProviderWebsiteLink website={data.website} />,
        });
    }
    if (!hasLocation && facts.length === 0) return null;
    return (
        <div className="mt-3 flex flex-col items-start gap-1.5 text-body-small text-doberman-black/55 md:flex-row md:flex-wrap md:items-center md:gap-x-2">
            {hasLocation && (
                <ProviderLocation
                    city={data.city}
                    distanceLabel={data.distanceLabel}
                />
            )}
            {facts.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {hasLocation && (
                        <span aria-hidden="true" className="hidden md:inline">
                            ·
                        </span>
                    )}
                    {facts.map((fact, index) => (
                        <Fragment key={fact.key}>
                            {index > 0 && META_SEPARATOR}
                            {fact.node}
                        </Fragment>
                    ))}
                </div>
            )}
        </div>
    );
}

export function ProviderHeader({ data }: { data: ProviderHeaderData }) {
    const rating = providerRating(data);
    const stats = getProviderStats(data, rating);

    return (
        <header className="flex min-w-0 flex-col">
            {data.breedSpecialty && (
                <p className="text-body text-doberman-black/80 text-pretty">
                    {data.breedSpecialty}
                </p>
            )}
            {data.providerName && (
                <h1
                    className={cn(
                        "text-page-title text-balance wrap-anywhere text-doberman-black",
                        data.breedSpecialty && "mt-2",
                    )}
                >
                    <ProviderVerifiedName
                        name={data.providerName}
                        isVerified={data.isVerified}
                        providerType={data.providerType}
                    />
                </h1>
            )}
            <ProviderMetaRow data={data} rating={rating} stats={stats} />
            {stats.length >= 2 && (
                <ProviderStatTiles
                    stats={stats}
                    className="mt-4 w-full max-w-md"
                />
            )}
        </header>
    );
}
